import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Maximize2, X } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine, PieChart, Pie, LabelList } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ChartModal from './ChartModal';
import { useFilters } from '../contexts/FilterContext';
import { productivityAnalyticsAPI } from '../services/api';

// Helper to format dates for API in local time (avoids timezone shifting issues)
const formatDateForApi = (date) => {
    if (!(date instanceof Date)) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const Productivity = ({ activeTab = 'Productivity' }) => {
    const { filters, manualTrigger, setAvailableMachines } = useFilters();

    // Derive x-axis label names based on selected equipment type:
    // '1' = Equipment → charts show "Equipment" / "Excavator" / "Operator"
    // '2' = Vehicle   → charts show "Vehicle"    / "Tipper"    / "Driver"
    const equipmentTypeLabel = filters.selectedEquipmentType === '2' ? 'Vehicle' : 'Equipment';
    const excavatorTypeLabel = filters.selectedEquipmentType === '2' ? 'Tipper' : 'Excavator';
    const operatorTypeLabel = filters.selectedEquipmentType === '2' ? 'Driver' : 'Operator';
    // State management for maximize functionality
    const [maximizedChartId, setMaximizedChartId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState({}); // Track sort order for each chart
    const [modalEquipmentFilter, setModalEquipmentFilter] = useState('all'); // Track equipment filter for modal
    const [equipmentProductivityData, setEquipmentProductivityData] = useState([]);
    const [operatorProductivityData, setOperatorProductivityData] = useState([]);
    const [equipmentAvailabilityData, setEquipmentAvailabilityData] = useState([]);
    const [operatorAvailabilityData, setOperatorAvailabilityData] = useState([]);
    const [equipmentAverageAvailabilityData, setEquipmentAverageAvailabilityData] = useState([]);
    const [equipmentAverageUtilizationData, setEquipmentAverageUtilizationData] = useState([]);
    const [operatorAverageUtilizationData, setOperatorAverageUtilizationData] = useState([]);
    const [operatorTargetTripsData, setOperatorTargetTripsData] = useState([]);
    const [excavatorTargetTripsData, setExcavatorTargetTripsData] = useState([]);
    const [totalProductivity, setTotalProductivity] = useState({
        value: 0,
        unit: 'CUM',
        percentage: 0,
        target: 0,
        date: '',
    });
    const [recentProductivity, setRecentProductivity] = useState([]);
    const [targetTrips, setTargetTrips] = useState({
        target: 0,
        blended: 0,
        actual: 0,
        unit: 'Trips',
    });
    const [productivityForecastData, setProductivityForecastData] = useState([]);
    const [availabilityForecastData, setAvailabilityForecastData] = useState([]);
    const [utilizationForecastData, setUtilizationForecastData] = useState([]);

    // State for drill-down functionality - track selected equipment name (for display) and ID (for API)
    const [selectedEquipmentForDrillDown, setSelectedEquipmentForDrillDown] = useState(null);
    const [selectedEquipmentIdForDrillDown, setSelectedEquipmentIdForDrillDown] = useState(null);

    // State for operator drill-down functionality - track selected operator name (for display) and ID (for API)
    const [selectedOperatorForDrillDown, setSelectedOperatorForDrillDown] = useState(null);
    const [selectedOperatorIdForDrillDown, setSelectedOperatorIdForDrillDown] = useState(null);

    // Track if Productivity component has loaded its initial data (LOCAL state, not global)
    const [productivityInitialDataLoaded, setProductivityInitialDataLoaded] = useState(false);

    // Track previous manualTrigger value to detect when arrow button is clicked
    const prevManualTriggerRef = useRef(manualTrigger);

    // Track previous selectedEquipmentIdForDrillDown to detect drill-down changes
    const prevSelectedEquipmentRef = useRef(selectedEquipmentIdForDrillDown);

    // Track previous selectedOperatorIdForDrillDown to detect operator drill-down changes
    const prevSelectedOperatorRef = useRef(selectedOperatorIdForDrillDown);

    // Track if API call is in progress to prevent duplicate calls
    const isFetchingRef = useRef(false);

    // Track if initial call has been made to prevent duplicate initial calls
    const initialCallMadeRef = useRef(false);

    // Fetch all productivity data from single API endpoint when filters change
    useEffect(() => {
        const fetchProductivityData = async () => {
            // Prevent duplicate calls - check and set flag synchronously
            if (isFetchingRef.current) {
                console.log('⏸️ API call already in progress, skipping duplicate request');
                return;
            }

            // Only fetch if Productivity is the active tab
            if (activeTab !== 'Productivity') {
                console.log('⏸️ Skipping Productivity API call - not active tab');
                return;
            }

            // Require companyId and locationId to be selected (both auto-selected on login)
            // Date range is optional - if only 'from' is set (today), API returns "till now" data
            // If both 'from' and 'to' are set, API filters by that date range
            if (!filters.selectedCompany) {
                console.log('⏸️ Skipping API call - company not selected');
                setLoading(false);
                return;
            }

            // Wait for locationId to be auto-selected (FilterBar auto-selects first location on login)
            if (!filters.selectedLocation) {
                console.log('⏸️ Skipping API call - location not yet auto-selected');
                setLoading(false);
                return;
            }

            // Check if drill-down changed (equipment or operator bar clicked)
            const equipmentDrillDownChanged = selectedEquipmentIdForDrillDown !== prevSelectedEquipmentRef.current;
            const operatorDrillDownChanged = selectedOperatorIdForDrillDown !== prevSelectedOperatorRef.current;
            const drillDownChanged = equipmentDrillDownChanged || operatorDrillDownChanged;

            // After initial load, only trigger API if:
            // 1. Manual trigger is activated (arrow button clicked), OR
            // 2. Drill-down changed (equipment or operator bar clicked)
            // On initial load (when company is first selected), automatically trigger API
            if (productivityInitialDataLoaded) {
                // Allow API call if drill-down changed
                if (drillDownChanged) {
                    prevSelectedEquipmentRef.current = selectedEquipmentIdForDrillDown;
                    prevSelectedOperatorRef.current = selectedOperatorIdForDrillDown;
                    if (equipmentDrillDownChanged) {
                        console.log('🔍 Drill-down changed - proceeding with API call for equipment ID:', selectedEquipmentIdForDrillDown);
                    }
                    if (operatorDrillDownChanged) {
                        console.log('🔍 Drill-down changed - proceeding with API call for operator ID:', selectedOperatorIdForDrillDown);
                    }
                } else {
                    // Check if manualTrigger actually changed (arrow button was clicked)
                    if (manualTrigger === prevManualTriggerRef.current) {
                        console.log('⏸️ Skipping auto API call - waiting for manual trigger (arrow button)');
                        return;
                    }
                    // Manual trigger changed, update ref and proceed with API call
                    prevManualTriggerRef.current = manualTrigger;
                    console.log('🔵 Manual trigger activated - proceeding with API call');
                }
            } else {
                // On initial load, check if we've already made the initial call
                if (initialCallMadeRef.current) {
                    console.log('⏸️ Initial API call already made, skipping duplicate');
                    return;
                }
                // Mark that initial call is being made (set synchronously before async operations)
                initialCallMadeRef.current = true;
            }

            // Set fetching flag synchronously before any async operations to prevent race conditions
            isFetchingRef.current = true;

            // Machine and shift are optional - include if selected, but don't require them
            // companyId and locationId are required (both auto-selected on login)

            try {
                setLoading(true);
                console.log('🔄 Fetching productivity analytics data with filters...');
                console.log('📋 Current filters:', filters);

                // Build API filters with correct parameter names
                const apiFilters = {};

                // Add companyId from selectedCompany (required - auto-selected on login)
                if (filters.selectedCompany) {
                    apiFilters.companyId = filters.selectedCompany;
                }

                // Add date filters if date range is set (both from and to)
                // On login, default is this month start date to today - API will filter by this range
                // If only 'from' is set (single date), don't pass dates - API will return "till now" data
                if (filters.dateRange?.from && filters.dateRange?.to) {
                    const startDate = formatDateForApi(filters.dateRange.from);
                    const endDate = formatDateForApi(filters.dateRange.to);
                    apiFilters.startDate = startDate;
                    apiFilters.endDate = endDate;
                    console.log('📅 Date range:', startDate, 'to', endDate);
                } else {
                    console.log('📅 No date range - showing "till now" data');
                }

                // Only add filters if user has explicitly selected them (not on initial login)
                // On login, only companyId and date range are passed - showing whole past year data

                // Add locationId only if user has selected location
                if (filters.selectedLocation) {
                    apiFilters.locationId = filters.selectedLocation;
                }

                // Add equipmentId only if user has selected machine
                if (filters.selectedMachine) {
                    apiFilters.equipmentId = filters.selectedMachine;
                    console.log('🔧 Machine filter applied:', filters.selectedMachine, '(mapped to equipmentId)');
                }

                // Add shift_no only if user has selected shift
                if (filters.selectedShift) {
                    apiFilters.shift_no = filters.selectedShift;
                }

                // Add equipment_type if selected
                if (filters.selectedEquipmentType) {
                    apiFilters.equipment_type = filters.selectedEquipmentType;
                }

                // Add equipment_id for drill-down functionality
                if (selectedEquipmentIdForDrillDown) {
                    apiFilters.equipment_id = selectedEquipmentIdForDrillDown;
                    console.log('🔍 Drill-down active for equipment_id:', selectedEquipmentIdForDrillDown);
                }

                // Add operator_id for operator drill-down functionality
                if (selectedOperatorIdForDrillDown) {
                    apiFilters.operator_id = selectedOperatorIdForDrillDown;
                    console.log('🔍 Drill-down active for operator ID:', selectedOperatorIdForDrillDown);
                }

                console.log('📤 API filters prepared:', apiFilters);

                const data = await productivityAnalyticsAPI.getAnalytics(apiFilters);

                // Mark initial data as loaded after first successful API call
                if (!productivityInitialDataLoaded) {
                    setProductivityInitialDataLoaded(true);
                    prevManualTriggerRef.current = manualTrigger; // Initialize ref
                    prevSelectedEquipmentRef.current = selectedEquipmentIdForDrillDown; // Initialize drill-down ref
                    prevSelectedOperatorRef.current = selectedOperatorForDrillDown; // Initialize operator drill-down ref
                    console.log('✅ Initial data loaded - subsequent filter changes require manual trigger');
                } else {
                    // Update refs after successful API call
                    prevManualTriggerRef.current = manualTrigger;
                    prevSelectedEquipmentRef.current = selectedEquipmentIdForDrillDown;
                    prevSelectedOperatorRef.current = selectedOperatorForDrillDown;
                }

                if (data) {
                    console.log('✅ Productivity analytics data received:', data);

                    // Transform and set Total Productivity
                    if (data.totalProductivity) {
                        setTotalProductivity({
                            value: data.totalProductivity.total || 0,
                            unit: data.totalProductivity.unit || 'CUM',
                            percentage: Number(data.totalProductivity.percentage) || 0,
                            target: data.totalProductivity.target || 0,
                            date: data.totalProductivity.period || 'Till Date'
                        });
                    }

                    // Transform and set Recent Productivity
                    if (data.recentProductivity && Array.isArray(data.recentProductivity)) {
                        const transformedRecent = data.recentProductivity.slice(0, 3).map((item, index) => ({
                            id: index + 1,
                            current: item.quantity || 0,
                            target: item.target || 0,
                            unit: item.unit || 'CUM',
                            percentage: item.percentage || 0,
                            date: item.date || 'Date'
                        }));
                        setRecentProductivity(transformedRecent);
                    }

                    // Transform and set Target vs Shortfall Trips
                    if (data.targetVsShortfallTrips) {
                        setTargetTrips({
                            target: data.targetVsShortfallTrips.targetTrips || 0,
                            blended: data.targetVsShortfallTrips.shortfallTrips || 0,
                            actual: data.targetVsShortfallTrips.actualTrips || 0,
                            unit: 'Trips'
                        });
                    }

                    // Transform and set Equipment Productivity vs Target
                    if (data.equipmentProductivityVsTarget && Array.isArray(data.equipmentProductivityVsTarget)) {
                        const transformedEquipProd = data.equipmentProductivityVsTarget.map(item => ({
                            equipmentId: item.equipmentId,
                            name: item.equipment,
                            productivity: item.productivity || 0,
                            target: item.target || 1
                        }));
                        setEquipmentProductivityData(transformedEquipProd);

                        // Update available machines in FilterContext for the machine filter dropdown
                        // This allows the filter to only show equipment that actually has data
                        const machinesForFilter = data.equipmentProductivityVsTarget
                            .filter(item => item.equipmentId && item.equipment)
                            .map(item => ({
                                machineId: item.equipmentId,
                                machineName: item.equipment
                            }));

                        // We only update the global list if we are not currently drilled down
                        // to keep the full list for that context available
                        if (machinesForFilter.length > 0 && !selectedEquipmentIdForDrillDown) {
                            setAvailableMachines(machinesForFilter);
                            console.log('📊 Updated available machines in FilterBar:', machinesForFilter.length);
                        }
                    }

                    // Transform and set Productivity Forecast
                    if (data.productivityForecast) {
                        const transformedForecast = [];
                        const currentData = data.productivityForecast.current || [];
                        const forecastData = data.productivityForecast.forecast || [];

                        // Create a map to combine current and forecast data by date
                        const dateMap = new Map();

                        // Add all current data points
                        currentData.forEach(item => {
                            const dateObj = new Date(item.date);
                            const dayLabel = `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;
                            dateMap.set(item.date, {
                                day: dayLabel,
                                date: item.date,
                                current: item.value,
                                forecast: null
                            });
                        });

                        // Add all forecast data points (merge with existing or create new)
                        forecastData.forEach(item => {
                            const dateObj = new Date(item.date);
                            const dayLabel = `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;

                            if (dateMap.has(item.date)) {
                                // Date exists, add forecast value
                                const existing = dateMap.get(item.date);
                                existing.forecast = item.value;
                            } else {
                                // New date, create entry
                                dateMap.set(item.date, {
                                    day: dayLabel,
                                    date: item.date,
                                    current: null,
                                    forecast: item.value
                                });
                            }
                        });

                        // Convert map to array and sort by date
                        transformedForecast.push(...Array.from(dateMap.values()));
                        transformedForecast.sort((a, b) => new Date(a.date) - new Date(b.date));

                        // Show all data points (both current and forecast) - don't limit
                        setProductivityForecastData(transformedForecast);
                    }

                    // Transform and set Equipment Availability
                    if (data.equipmentAvailability && Array.isArray(data.equipmentAvailability)) {
                        const transformedEquipAvail = data.equipmentAvailability.map(item => ({
                            equipmentId: item.equipmentId,
                            name: item.equipment,
                            availability: item.availability || 0,
                            target: item.target || 100
                        }));
                        setEquipmentAvailabilityData(transformedEquipAvail);
                    }

                    // Transform and set Availability Forecast
                    if (data.availabilityForecast) {
                        const transformedAvailForecast = [];
                        const currentData = data.availabilityForecast.current || [];
                        const forecastData = data.availabilityForecast.forecast || [];

                        // Create a map to combine current and forecast data by date
                        const dateMap = new Map();

                        // Add all current data points
                        currentData.forEach(item => {
                            const dateObj = new Date(item.date);
                            const dayLabel = `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;
                            dateMap.set(item.date, {
                                day: dayLabel,
                                date: item.date,
                                current: item.value,
                                forecast: null
                            });
                        });

                        // Add all forecast data points (merge with existing or create new)
                        forecastData.forEach(item => {
                            const dateObj = new Date(item.date);
                            const dayLabel = `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;

                            if (dateMap.has(item.date)) {
                                // Date exists, add forecast value
                                const existing = dateMap.get(item.date);
                                existing.forecast = item.value;
                            } else {
                                // New date, create entry
                                dateMap.set(item.date, {
                                    day: dayLabel,
                                    date: item.date,
                                    current: null,
                                    forecast: item.value
                                });
                            }
                        });

                        // Convert map to array and sort by date
                        transformedAvailForecast.push(...Array.from(dateMap.values()));
                        transformedAvailForecast.sort((a, b) => new Date(a.date) - new Date(b.date));

                        // Show all data points (both current and forecast) - don't limit
                        setAvailabilityForecastData(transformedAvailForecast);
                    }

                    // Transform and set Operator Availability
                    if (data.operatorAvailability && Array.isArray(data.operatorAvailability)) {
                        const transformedOpAvail = data.operatorAvailability.map(item => ({
                            name: item.operator,
                            operatorId: item.operatorId,
                            empCode: item.empCode || '',
                            averageUtilization: item.availability || 0,
                            target: item.target || 100
                        }));
                        setOperatorAvailabilityData(transformedOpAvail);
                    }

                    // Transform and set Operator Productivity vs Target
                    if (data.operatorProductivityVsTarget && Array.isArray(data.operatorProductivityVsTarget)) {
                        const transformedOpProd = data.operatorProductivityVsTarget.map(item => ({
                            name: item.operator,
                            operatorId: item.operatorId,
                            empCode: item.empCode || '',
                            productivity: item.productivity || 0,
                            target: item.target || 1
                        }));
                        setOperatorProductivityData(transformedOpProd);
                    }

                    // Transform and set Equipment Average Availability
                    if (data.equipmentAverageAvailability && Array.isArray(data.equipmentAverageAvailability)) {
                        const transformedEquipAvgAvail = data.equipmentAverageAvailability.map(item => ({
                            equipmentId: item.equipmentId,
                            name: item.equipment,
                            productivityTarget: item.availability || 0,
                            target: item.target || 100
                        }));
                        setEquipmentAverageAvailabilityData(transformedEquipAvgAvail);
                    }

                    // Transform and set Equipment Average Utilization
                    if (data.equipmentAverageUtilization && Array.isArray(data.equipmentAverageUtilization)) {
                        const transformedEquipAvgUtil = data.equipmentAverageUtilization.map(item => ({
                            equipmentId: item.equipmentId,
                            name: item.equipment,
                            averageUtilization: item.utilization || 0,
                            target: item.target || 100
                        }));
                        setEquipmentAverageUtilizationData(transformedEquipAvgUtil);
                    }

                    // Transform and set Operator Average Utilization
                    if (data.operatorAverageUtilization && Array.isArray(data.operatorAverageUtilization)) {
                        const transformedOpAvgUtil = data.operatorAverageUtilization.map(item => ({
                            name: item.operator,
                            operatorId: item.operatorId,
                            empCode: item.empCode || '',
                            productivity: item.utilization || 0,
                            target: item.target || 100
                        }));
                        setOperatorAverageUtilizationData(transformedOpAvgUtil);
                    }

                    // Transform and set Operator Target Trips and Achieved (using operatorTripsSummary)
                    if (data.operatorTripsSummary && Array.isArray(data.operatorTripsSummary)) {
                        const transformedOpTargetTrips = data.operatorTripsSummary.map(item => ({
                            name: item.operator_name,
                            operatorId: item.operator_id,
                            empCode: item.emp_code || '',
                            empCodeType: item.emp_code_type,
                            targetTrips: item.targetTrips || 0,
                            achievedTrips: item.achievedTrips || 0
                        }));
                        setOperatorTargetTripsData(transformedOpTargetTrips);
                    }

                    // Transform and set Excavator Target Trips and Achieved (using excavatorTripsSummary)
                    if (data.excavatorTripsSummary && Array.isArray(data.excavatorTripsSummary)) {
                        const transformedExcavatorTrips = data.excavatorTripsSummary.map(item => ({
                            name: item.equipment_registration_no,
                            equipmentId: item.equipment_id,
                            equipmentType: item.equipment_type,
                            targetTrips: item.targetTrips || 0,
                            achievedTrips: item.achievedTrips || 0
                        }));
                        setExcavatorTargetTripsData(transformedExcavatorTrips);
                    }

                    // Transform and set Utilization Forecast
                    if (data.utilizationForecast) {
                        const transformedUtilForecast = [];
                        const currentData = data.utilizationForecast.current || [];
                        const forecastData = data.utilizationForecast.forecast || [];

                        // Create a map to combine current and forecast data by date
                        const dateMap = new Map();

                        // Add all current data points
                        currentData.forEach(item => {
                            const dateObj = new Date(item.date);
                            const dayLabel = `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;
                            dateMap.set(item.date, {
                                day: dayLabel,
                                date: item.date,
                                current: item.value,
                                forecast: null
                            });
                        });

                        // Add all forecast data points (merge with existing or create new)
                        forecastData.forEach(item => {
                            const dateObj = new Date(item.date);
                            const dayLabel = `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;

                            if (dateMap.has(item.date)) {
                                // Date exists, add forecast value
                                const existing = dateMap.get(item.date);
                                existing.forecast = item.value;
                            } else {
                                // New date, create entry
                                dateMap.set(item.date, {
                                    day: dayLabel,
                                    date: item.date,
                                    current: null,
                                    forecast: item.value
                                });
                            }
                        });

                        // Convert map to array and sort by date
                        transformedUtilForecast.push(...Array.from(dateMap.values()));
                        transformedUtilForecast.sort((a, b) => new Date(a.date) - new Date(b.date));

                        // Show all data points (both current and forecast) - don't limit
                        setUtilizationForecastData(transformedUtilForecast);
                    }

                    console.log('✅ All productivity data transformed and set');
                } else {
                    console.warn('⚠️ No data received from productivity analytics API');
                }
            } catch (error) {
                console.error('❌ Failed to fetch productivity analytics:', error);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        fetchProductivityData();
    }, [
        filters.selectedCompany,
        filters.dateRange?.from?.getTime(), // Trigger when date range changes
        filters.dateRange?.to?.getTime(), // Trigger when date range changes
        filters.selectedMachine, // Include machine filter to ensure latest value is used
        filters.selectedLocation, // Include location filter to ensure latest value is used
        filters.selectedShift, // Include shift filter to ensure latest value is used
        filters.selectedEquipmentType, // Trigger when equipment type filter changes
        manualTrigger, // Trigger API when arrow button is clicked
        selectedEquipmentIdForDrillDown, // Trigger API when drill-down equipment ID changes
        selectedOperatorIdForDrillDown, // Trigger API when drill-down operator ID changes
        // NOTE: productivityInitialDataLoaded intentionally NOT included to prevent double API calls
        // The initialCallMadeRef handles preventing duplicate initial calls internally
        activeTab // Only fetch when Productivity is the active tab
    ]);

    // Handler for equipment drill-down - click on equipment name in chart
    const handleEquipmentDrillDown = useCallback((equipmentName, equipmentId) => {
        console.log('🔍 Drill-down clicked for equipment:', equipmentName, 'ID:', equipmentId);
        setSelectedEquipmentForDrillDown(equipmentName);
        setSelectedEquipmentIdForDrillDown(equipmentId);
        // The useEffect will automatically trigger API call with equipment_id
    }, []);

    // Handler for operator drill-down - click on operator name in chart
    const handleOperatorDrillDown = useCallback((operatorName, operatorId) => {
        console.log('🔍 Drill-down clicked for operator:', operatorName, 'ID:', operatorId);
        setSelectedOperatorForDrillDown(operatorName);
        setSelectedOperatorIdForDrillDown(operatorId);
        // The useEffect will automatically trigger API call with operator_id
    }, []);

    // Handler to clear equipment drill-down
    const handleClearDrillDown = useCallback(() => {
        console.log('🔍 Clearing equipment drill-down');
        setSelectedEquipmentForDrillDown(null);
        setSelectedEquipmentIdForDrillDown(null);
        // The useEffect will automatically trigger API call without equipment_id
    }, []);

    // Handler to clear operator drill-down
    const handleClearOperatorDrillDown = useCallback(() => {
        console.log('🔍 Clearing operator drill-down');
        setSelectedOperatorForDrillDown(null);
        setSelectedOperatorIdForDrillDown(null);
        // The useEffect will automatically trigger API call without operator_id
    }, []);

    const pieData = useMemo(() => {
        const target = targetTrips.target || 0;
        const shortfall = targetTrips.blended || 0;
        const actual = targetTrips.actual || 0;

        if (target === 0) return [];

        // Correct logic for a progress donut:
        // The total is the Target.
        // The components are what's been done (actual) and what's left (shortfall).
        const actualPercent = (actual / target * 100).toFixed(2);
        const shortfallPercent = (100 - parseFloat(actualPercent)).toFixed(2);

        return [
            { name: 'Achieved Trips (%)', value: parseFloat(actualPercent), fill: '#00498A' },
            { name: 'Target Trips (%)', value: parseFloat(shortfallPercent), fill: '#fdb205ff' }
        ].filter(item => item.value > 0);
    }, [targetTrips.target, targetTrips.blended, targetTrips.actual]);


    // Chart configurations - maps chart IDs to their properties
    const chartConfigs = {
        'equipment-productivity': {
            title: `${equipmentTypeLabel} Productivity vs Target`,
            dataKey: 'equipmentProductivity',
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'productivity',
                fill: '#3b82f6',
                radius: [4, 4, 0, 0],
                barSize: 34.94,
                referenceLine: { y: 90, stroke: '#ef4444', strokeDasharray: '3 3' },
                xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: equipmentTypeLabel, position: 'bottom', offset: 5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Productivity Target', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'productivity-forecast': {
            title: 'Productivity Forecast Next 10 Days',
            dataKey: 'productivityForecast',
            chartType: 'line',
            chartProps: {
                xAxis: { dataKey: 'day', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Productivity', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } },
                lines: [
                    { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
                    { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
                ]
            }
        },
        'equipment-availability': {
            title: `${equipmentTypeLabel} Availability`,
            dataKey: 'equipmentAvailability',
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'availability',
                fill: '#14b8a6',
                radius: [4, 4, 0, 0],
                barSize: 34.94,
                referenceLine: { y: 95, stroke: '#10b981', strokeDasharray: '3 3' },
                xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: equipmentTypeLabel, position: 'bottom', offset: 5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Availability', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'availability-forecast': {
            title: 'Availability Forecast Next 10 Days',
            dataKey: 'availabilityForecast',
            chartType: 'line',
            chartProps: {
                xAxis: { dataKey: 'day', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Availability', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } },
                lines: [
                    { dataKey: 'current', stroke: '#14b8a6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
                    { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
                ]
            }
        },
        'utilization-forecast': {
            title: 'Utilization Forecast Next 10 days',
            dataKey: 'utilizationForecast',
            chartType: 'line',
            chartProps: {
                xAxis: { dataKey: 'day', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Utilization', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } },
                lines: [
                    { dataKey: 'current', stroke: '#0594bc', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
                    { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
                ]
            }
        },
        'operator-availability': {
            title: `${operatorTypeLabel} Availability`,
            dataKey: 'operatorAvailability',
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'averageUtilization',
                fill: '#14b8a6',
                radius: [4, 4, 0, 0],
                barSize: 34.94,
                referenceLine: { y: 550, stroke: '#ef4444', strokeDasharray: '3 3' },
                xAxis: {
                    dataKey: 'name',
                    tick: { fontSize: 12 },
                    interval: 0,
                    tickFormatter: (value) => value.replace('Operator ', 'Op '),
                    label: { value: operatorTypeLabel, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
                },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Average Utilization', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'operator-productivity-vs-target': {
            title: `${operatorTypeLabel} Productivity vs Target`,
            dataKey: 'operatorProductivityVsTarget',
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'productivity',
                fill: '#0594bc',
                radius: [4, 4, 0, 0],
                barSize: 34.94,
                referenceLine: { y: 1.15, stroke: '#ef4444', strokeDasharray: '3 3' },
                xAxis: {
                    dataKey: 'name',
                    tick: { fontSize: 12 },
                    interval: 0,
                    tickFormatter: (value) => value.replace('Operator ', 'Op '),
                    label: { value: operatorTypeLabel, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
                },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Productivity Target', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'equipment-average-availability': {
            title: `${equipmentTypeLabel} Average Availability`,
            dataKey: 'equipmentAverageAvailability',
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'productivityTarget',
                fill: '#60a5fa',
                radius: [4, 4, 0, 0],
                barSize: 34.94,
                referenceLine: { y: 1.15, stroke: '#ef4444', strokeDasharray: '3 3' },
                xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: equipmentTypeLabel, position: 'insideBottom', offset: -5 } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Productivity Target', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'equipment-average-utilization': {
            title: `${equipmentTypeLabel} Average Utilization`,
            dataKey: 'equipmentAverageUtilization',
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'averageUtilization',
                fill: '#f97316',
                radius: [4, 4, 0, 0],
                barSize: 34.94,
                referenceLine: { y: 1.05, stroke: '#ef4444', strokeDasharray: '3 3' },
                xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: equipmentTypeLabel, position: 'insideBottom', offset: -5 } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Average Utilization', angle: -90, position: 'insideLeft' } }
            }
        },
        'operator-average-utilization': {
            title: `${operatorTypeLabel} Average Utilization`,
            dataKey: 'equipmentProductivity', // Reusing equipmentProductivity data for this chart
            chartType: 'bar',
            chartProps: {
                barCategoryGap: '10%',
                dataKey: 'productivity',
                fill: '#0594bc',
                radius: [6, 6, 0, 0],
                barSize: 34.94,
                margin: { top: 18, right: 8, left: 4, bottom: 22 },
                referenceLine: { y: 90, stroke: '#ef4444', strokeWidth: 2 },
                xAxis: { dataKey: 'name', tick: { fontSize: 11 }, interval: 0, dy: 8, label: { value: operatorTypeLabel, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 11 } } },
                yAxis: { tick: { fontSize: 11 }, dx: -3, label: { value: 'Average Utilization', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'operator-target-trips': {
            title: `Target trips and achieved by ${operatorTypeLabel} name`,
            dataKey: 'operatorTargetTrips',
            chartType: 'clusteredBar',
            chartProps: {
                barCategoryGap: '10%',
                bars: [
                    { dataKey: 'targetTrips', fill: '#3b82f6', name: 'Target Trips' },
                    { dataKey: 'achievedTrips', fill: '#10b981', name: 'Achieved Trips' }
                ],
                radius: [4, 4, 0, 0],
                barSize: 62.29,
                xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: operatorTypeLabel, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Trips', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        },
        'excavator-target-trips': {
            title: `Target trips and achieved by ${excavatorTypeLabel}`,
            dataKey: 'excavatorTargetTrips',
            chartType: 'clusteredBar',
            chartProps: {
                barCategoryGap: '10%',
                bars: [
                    { dataKey: 'targetTrips', fill: '#f59e0b', name: 'Target Trips' },
                    { dataKey: 'achievedTrips', fill: '#8b5cf6', name: 'Achieved Trips' }
                ],
                radius: [4, 4, 0, 0],
                barSize: 62.29,
                xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: excavatorTypeLabel, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
                yAxis: { tick: { fontSize: 12 }, label: { value: 'Trips', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
            }
        }
    };

    const handleMaximize = useCallback((chartId) => {
        const config = chartConfigs[chartId];
        if (!config) return;
        setMaximizedChartId(chartId);
    }, []);

    const handleCloseModal = useCallback(() => {
        setMaximizedChartId(null);
        setModalEquipmentFilter('all'); // Reset equipment filter when modal closes
    }, []);

    // Get unique equipment list for the current chart
    const getEquipmentListForChart = useCallback((chartId) => {
        let dataSource = [];
        if (chartId === 'equipment-productivity') {
            dataSource = equipmentProductivityData || [];
        } else if (chartId === 'equipment-availability') {
            dataSource = equipmentAvailabilityData || [];
        } else if (chartId === 'equipment-average-availability') {
            dataSource = equipmentAverageAvailabilityData || [];
        } else if (chartId === 'equipment-average-utilization') {
            dataSource = equipmentAverageUtilizationData || [];
        } else if (chartId === 'operator-availability') {
            dataSource = operatorAvailabilityData || [];
        } else if (chartId === 'operator-productivity-vs-target') {
            dataSource = operatorProductivityData || [];
        } else if (chartId === 'operator-average-utilization') {
            dataSource = operatorAverageUtilizationData || [];
        } else if (chartId === 'operator-target-trips') {
            dataSource = operatorTargetTripsData || [];
        } else if (chartId === 'excavator-target-trips') {
            dataSource = excavatorTargetTripsData || [];
        }
        return [...new Set(dataSource.map(item => item.name).filter(Boolean))];
    }, [equipmentProductivityData, equipmentAvailabilityData, equipmentAverageAvailabilityData, equipmentAverageUtilizationData, operatorAvailabilityData, operatorProductivityData, operatorAverageUtilizationData, operatorTargetTripsData, excavatorTargetTripsData]);

    const renderOperatorTripsTooltip = useCallback(({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;

        const entry = payload[0]?.payload || {};
        const empCode = entry.empCode || entry.emp_code || entry.empCodeType || '-';
        const operatorName = entry.name || entry.operator_name || '-';
        const achievedTrips = Number(entry.achievedTrips || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
        const targetTrips = Number(entry.targetTrips || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

        return (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 180 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>Emp Code:</span> {empCode}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>Name:</span> {operatorName}
                </div>
                <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: '#10b981' }}>Achieved Trips</span>
                    <span style={{ fontWeight: 700, color: '#111827' }}>{achievedTrips}</span>
                </div>
                <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: '#3b82f6' }}>Target Trips</span>
                    <span style={{ fontWeight: 700, color: '#111827' }}>{targetTrips}</span>
                </div>
            </div>
        );
    }, []);


    const renderMaximizedChart = (chartId) => {
        const config = chartConfigs[chartId];
        if (!config) return null;

        // Use API data for all charts
        let data = [];
        if (chartId === 'equipment-productivity') {
            data = equipmentProductivityData || [];
        } else if (chartId === 'productivity-forecast') {
            data = productivityForecastData || [];
        } else if (chartId === 'equipment-availability') {
            data = equipmentAvailabilityData || [];
        } else if (chartId === 'availability-forecast') {
            data = availabilityForecastData || [];
        } else if (chartId === 'utilization-forecast') {
            data = utilizationForecastData || [];
        } else if (chartId === 'operator-availability') {
            data = operatorAvailabilityData || [];
        } else if (chartId === 'operator-productivity-vs-target') {
            data = operatorProductivityData || [];
        } else if (chartId === 'equipment-average-availability') {
            data = equipmentAverageAvailabilityData || [];
        } else if (chartId === 'equipment-average-utilization') {
            data = equipmentAverageUtilizationData || [];
        } else if (chartId === 'operator-average-utilization') {
            data = operatorAverageUtilizationData || [];
        } else if (chartId === 'operator-target-trips') {
            data = operatorTargetTripsData || [];
        } else if (chartId === 'excavator-target-trips') {
            data = excavatorTargetTripsData || [];
        }

        // Apply equipment filter if not "all"
        if (modalEquipmentFilter !== 'all' && (config.chartType === 'bar' || config.chartType === 'clusteredBar')) {
            data = data.filter(item => item.name === modalEquipmentFilter);
        }

        // Apply sorting if sort order is set
        const currentSortOrder = sortOrder[chartId] || 'low-high';
        if (config.chartType === 'bar' && currentSortOrder && data && data.length > 0) {
            const sortDataKey = config.chartProps.dataKey;
            if (sortDataKey) {
                // Create a new array to ensure React detects the change
                const sortedData = [...data].sort((a, b) => {
                    const aValue = Number(a[sortDataKey]) || 0;
                    const bValue = Number(b[sortDataKey]) || 0;
                    if (currentSortOrder === 'low-high') {
                        return aValue - bValue;
                    } else {
                        return bValue - aValue;
                    }
                });
                data = sortedData;
            }
        } else if (config.chartType === 'clusteredBar' && currentSortOrder && data && data.length > 0) {
            // Sort clustered bar charts by targetTrips
            const sortedData = [...data].sort((a, b) => {
                const aValue = Number(a.targetTrips) || 0;
                const bValue = Number(b.targetTrips) || 0;
                if (currentSortOrder === 'low-high') {
                    return aValue - bValue;
                } else {
                    return bValue - aValue;
                }
            });
            data = sortedData;
        }
        // NOTE: Line charts (utilization-forecast, etc.) are NOT re-sorted here.
        // They must remain in chronological date order as set during data transformation.

        // Define equipment charts that need drill-down functionality
        const equipmentCharts = ['equipment-productivity', 'equipment-availability', 'equipment-average-availability', 'equipment-average-utilization'];
        const isEquipmentChart = equipmentCharts.includes(chartId);

        if (config.chartType === 'bar') {
            const props = config.chartProps;

            // Check if this is an operator chart that needs dynamic width calculation
            const operatorCharts = ['operator-availability', 'operator-productivity-vs-target', 'operator-average-utilization'];
            const isOperatorChart = operatorCharts.includes(chartId);

            // Calculate minimum width based on number of bars
            let minChartWidth;
            if (isOperatorChart) {
                // Calculate dynamic width per bar based on longest label - ensure full labels fit
                const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                // Calculate width to fit full labels: ~10px per character for horizontal text
                const widthPerBar = Math.max(120, maxLabelLength * 10);
                minChartWidth = Math.max(1000, data.length * widthPerBar);
            } else {
                // Each bar needs approximately 100px (bar width 62.29 + gap 35%)
                const estimatedWidthPerBar = 100; // Approximate space per bar including gap
                minChartWidth = Math.max(1000, data.length * estimatedWidthPerBar);
            }

            // Increase gap between bars in maximized view for better readability
            const maximizedBarCategoryGap = '25%';
            // Set bar width slightly smaller for maximized view to look less "oversized"
            const maximizedBarSize = 48;

            // Ensure margin includes left space for Y-axis labels
            const chartMargin = props.margin || {};
            const defaultMargin = {
                top: chartMargin.top ?? 5,
                right: chartMargin.right ?? 10,
                left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
                bottom: chartMargin.bottom ?? 30
            };

            // Create modified axis props with larger font for maximized view
            const xAxisPropsWithoutLabel = {
                ...props.xAxis,
                tick: {
                    ...(props.xAxis?.tick || {}),
                    fontSize: 14 // Increased from 12
                }
            };
            delete xAxisPropsWithoutLabel.label;
            // Remove tickFormatter for operator charts to show full labels
            if (isOperatorChart && xAxisPropsWithoutLabel.tickFormatter) {
                delete xAxisPropsWithoutLabel.tickFormatter;
            }

            const yAxisPropsWithoutLabel = {
                ...props.yAxis,
                tick: {
                    ...(props.yAxis?.tick || {}),
                    fontSize: 14 // Increased from 12
                }
            };
            delete yAxisPropsWithoutLabel.label;

            return (
                <div className="w-full h-full relative flex flex-col" key={`chart-container-${chartId}-${currentSortOrder}`}>
                    {/* Fixed Y-axis label - positioned absolutely on the left */}
                    {props.yAxis?.label && (
                        <div
                            className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
                            style={{
                                width: '60px',
                                paddingLeft: '15px'
                            }}
                        >
                            <div
                                style={{
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    transform: 'rotate(180deg)',
                                    fontSize: '13px', // Increased from 11px
                                    fontWeight: 600,
                                    color: '#374151',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {props.yAxis.label.value || 'Y-Axis'}
                            </div>
                        </div>
                    )}

                    {/* Scrollable chart container */}
                    <div
                        className="flex-1 overflow-x-auto overflow-y-hidden relative"
                        style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: props.xAxis?.label ? '30px' : '0' }}
                    >
                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%', position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%" key={`chart-${chartId}-${currentSortOrder}`}>
                                <BarChart data={data} barCategoryGap={maximizedBarCategoryGap} margin={defaultMargin}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis {...xAxisPropsWithoutLabel} />
                                    <YAxis {...yAxisPropsWithoutLabel} />
                                    <Tooltip
                                        cursor={false}
                                        content={isOperatorChart ? ({ active, payload }) => {
                                            if (!active || !payload || !payload.length) return null;
                                            const entry = payload[0].payload;
                                            return (
                                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 150 }}>
                                                    {entry.empCode && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}><span style={{ fontWeight: 600, color: '#374151' }}>Emp Code:</span> {entry.empCode}</div>}
                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}><span style={{ fontWeight: 600, color: '#374151' }}>Name:</span> {entry.name}</div>
                                                    {payload.map((p, i) => (
                                                        <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                            <span style={{ color: p.color || '#374151' }}>{p.name}</span>
                                                            <span style={{ fontWeight: 700, color: '#111827' }}>{p.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        } : undefined}
                                    />
                                    {props.referenceLine && (
                                        <ReferenceLine
                                            y={props.referenceLine.y}
                                            stroke={props.referenceLine.stroke}
                                            strokeDasharray={props.referenceLine.strokeDasharray}
                                            strokeWidth={props.referenceLine.strokeWidth}
                                        />
                                    )}
                                    <Bar
                                        dataKey={props.dataKey}
                                        fill={props.fill}
                                        radius={props.radius}
                                        barSize={maximizedBarSize}
                                        activeBar={false}
                                        onClick={(barData) => {
                                            // Enable drill-down for equipment charts (productivity, availability, utilization)
                                            if (isEquipmentChart && barData && barData.name) {
                                                console.log('🔍 Equipment bar clicked:', barData.name, barData.equipmentId);
                                                handleEquipmentDrillDown(barData.name, barData.equipmentId);
                                            } else if (isOperatorChart && barData && barData.name) {
                                                handleOperatorDrillDown(barData.name, barData.operatorId);
                                            }
                                        }}
                                    >
                                        {/* Equipment charts - show clickable cells with highlight on selection */}
                                        {isEquipmentChart && data && data.map((entry, index) => {
                                            let highlightColor;
                                            if (entry.name === selectedEquipmentForDrillDown) {
                                                // Darker highlight color when selected
                                                if (chartId === 'equipment-productivity') {
                                                    highlightColor = "#1e40af"; // Dark blue
                                                } else if (chartId === 'equipment-availability') {
                                                    highlightColor = "#0d9488"; // Dark teal
                                                } else if (chartId === 'equipment-average-availability') {
                                                    highlightColor = "#2563eb"; // Dark blue
                                                } else if (chartId === 'equipment-average-utilization') {
                                                    highlightColor = "#c2410c"; // Dark orange
                                                } else {
                                                    highlightColor = props.fill;
                                                }
                                            } else {
                                                highlightColor = props.fill;
                                            }
                                            return (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={highlightColor}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            );
                                        })}
                                        {/* Operator charts - show clickable cells with highlight on selection */}
                                        {isOperatorChart && data && data.map((entry, index) => {
                                            let highlightColor;
                                            if (chartId === 'operator-availability') {
                                                highlightColor = entry.name === selectedOperatorForDrillDown ? "#0d9488" : props.fill;
                                            } else if (chartId === 'operator-productivity-vs-target') {
                                                highlightColor = entry.name === selectedOperatorForDrillDown ? "#0369a1" : props.fill;
                                            } else if (chartId === 'operator-average-utilization') {
                                                highlightColor = entry.name === selectedOperatorForDrillDown ? "#0369a1" : props.fill;
                                            } else {
                                                highlightColor = props.fill;
                                            }
                                            return (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={highlightColor}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            );
                                        })}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Fixed X-axis label - positioned absolutely at the bottom */}
                    {props.xAxis?.label && (
                        <div
                            className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
                            style={{
                                height: '30px',
                                marginLeft: props.yAxis?.label ? '60px' : '0',
                                fontSize: '14px', // Increased from 12px
                                fontWeight: 600,
                                color: '#374151'
                            }}
                        >
                            {props.xAxis.label.value || 'X-Axis'}
                        </div>
                    )}
                </div>
            );
        } else if (config.chartType === 'line') {
            const props = config.chartProps;
            // Determine colors for indicators based on chart type
            const currentColor = props.lines?.[0]?.stroke || '#3b82f6';
            const forecastColor = props.lines?.[1]?.stroke || '#f97316';

            // Ensure margin includes left space for Y-axis labels
            const chartMargin = props.margin || {};
            const defaultMargin = {
                top: chartMargin.top ?? 5,
                right: chartMargin.right ?? 20,
                left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
                bottom: chartMargin.bottom ?? 20
            };

            // Create modified axis props without labels (we'll render them separately as fixed overlays)
            const xAxisPropsWithoutLabel = { ...props.xAxis };
            delete xAxisPropsWithoutLabel.label;

            const yAxisPropsWithoutLabel = { ...props.yAxis };
            delete yAxisPropsWithoutLabel.label;

            return (
                <div className="w-full h-full relative flex flex-col">
                    {/* Fixed Y-axis label - positioned absolutely on the left */}
                    {props.yAxis?.label && (
                        <div
                            className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
                            style={{
                                width: '60px',
                                paddingLeft: '15px',
                                paddingBottom: '40px'
                            }}
                        >
                            <div
                                style={{
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    transform: 'rotate(180deg)',
                                    fontSize: '13px', // Increased from 11px
                                    fontWeight: 600,
                                    color: '#374151',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {props.yAxis.label.value || 'Y-Axis'}
                            </div>
                        </div>
                    )}

                    {/* Scrollable chart container */}
                    <div
                        className="flex-1 overflow-x-auto overflow-y-hidden relative"
                        style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: props.xAxis?.label ? '60px' : '40px' }}
                    >
                        <ResponsiveContainer width="100%" height="100%" key={`line-chart-${chartId}-${sortOrder[chartId] || 'low-high'}`}>
                            <LineChart data={data} margin={defaultMargin}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis {...xAxisPropsWithoutLabel} />
                                <YAxis {...yAxisPropsWithoutLabel} />
                                <Tooltip />
                                {props.lines.map((line, index) => (
                                    <Line
                                        key={index}
                                        type="monotone"
                                        dataKey={line.dataKey}
                                        stroke={line.stroke}
                                        strokeWidth={line.strokeWidth}
                                        strokeDasharray={line.strokeDasharray}
                                        dot={line.dot}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Fixed X-axis label - positioned first, below chart */}
                    {props.xAxis?.label && (
                        <div
                            className="absolute bottom-[40px] left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
                            style={{
                                height: '30px',
                                marginLeft: props.yAxis?.label ? '60px' : '0',
                                fontSize: '14px', // Increased from 12px
                                fontWeight: 600,
                                color: '#374151'
                            }}
                        >
                            {props.xAxis.label.value || 'X-Axis'}
                        </div>
                    )}

                    {/* Legend - positioned below X-axis label */}
                    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-6 pb-4" style={{ marginLeft: props.yAxis?.label ? '60px' : '0' }}>
                        <div className="flex items-center text-xs text-gray-600">
                            <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: currentColor }}></span>
                            Current
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                            <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: forecastColor }}></span>
                            Forecast
                        </div>
                    </div>
                </div>
            );
        } else if (config.chartType === 'clusteredBar') {
            const props = config.chartProps;

            // Check if this is an operator chart that needs dynamic width calculation
            const isOperatorChart = chartId.includes('operator');

            // Calculate minimum width based on number of bars
            let minChartWidth;
            if (isOperatorChart) {
                // Calculate dynamic width per bar based on longest label - ensure full labels fit
                const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                // Calculate width to fit full labels: ~10px per character for horizontal text
                const widthPerBar = Math.max(120, maxLabelLength * 10);
                minChartWidth = Math.max(1000, data.length * widthPerBar);
            } else {
                // Each bar group needs approximately 150px for enough gap between groups
                const estimatedWidthPerBar = 150; // Approximate space per bar including gap
                minChartWidth = Math.max(1000, data.length * estimatedWidthPerBar);
            }

            // Increase gap between bar groups in maximized view for better readability
            const maximizedBarCategoryGap = '70%';
            // Set smaller bar width for maximized view to allow more gap
            const maximizedBarSize = 40;

            // Ensure margin includes left space for Y-axis labels
            const chartMargin = props.margin || {};
            const defaultMargin = {
                top: chartMargin.top ?? 5,
                right: chartMargin.right ?? 10,
                left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
                bottom: chartMargin.bottom ?? 30
            };

            // Create modified axis props without labels (we'll render them separately as fixed overlays)
            const xAxisPropsWithoutLabel = { ...props.xAxis };
            delete xAxisPropsWithoutLabel.label;

            const yAxisPropsWithoutLabel = { ...props.yAxis };
            delete yAxisPropsWithoutLabel.label;

            return (
                <div className="w-full h-full relative flex flex-col">
                    {/* Fixed Y-axis label - positioned absolutely on the left */}
                    {props.yAxis?.label && (
                        <div
                            className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
                            style={{
                                width: '60px',
                                paddingLeft: '15px',
                                paddingBottom: '40px'
                            }}
                        >
                            <div
                                style={{
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    transform: 'rotate(180deg)',
                                    fontSize: '13px', // Increased from 11px
                                    fontWeight: 600,
                                    color: '#374151',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {props.yAxis.label.value || 'Y-Axis'}
                            </div>
                        </div>
                    )}

                    {/* Scrollable chart container */}
                    <div
                        className="flex-1 overflow-x-auto overflow-y-hidden relative"
                        style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: props.xAxis?.label ? '30px' : '0' }}
                    >
                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%', position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%" key={`chart-${chartId}-${currentSortOrder}`}>
                                <BarChart
                                    data={data}
                                    barCategoryGap={maximizedBarCategoryGap}
                                    barGap={8}
                                    margin={defaultMargin}
                                    onClick={(chartData) => {
                                        try {
                                            if (chartData && chartData.activePayload && chartData.activePayload.length > 0) {
                                                const payload = chartData.activePayload[0].payload;
                                                console.log('🔍 BarChart clicked:', { payload, chartId, isOperatorChart });

                                                // Handle operator charts
                                                if ((isOperatorChart || chartId === 'operator-target-trips') && payload && payload.name) {
                                                    console.log('🔍 Triggering operator drill-down from BarChart:', payload.name, payload.operatorId);
                                                    handleOperatorDrillDown(payload.name, payload.operatorId);
                                                }
                                                // Handle excavator chart
                                                else if (chartId === 'excavator-target-trips' && payload && payload.name) {
                                                    console.log('🔍 Triggering equipment drill-down from BarChart:', payload.name, payload.equipmentId);
                                                    handleEquipmentDrillDown(payload.name, payload.equipmentId);
                                                }
                                            }
                                        } catch (err) {
                                            console.error('BarChart click error:', err);
                                        }
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis {...xAxisPropsWithoutLabel} />
                                    <YAxis {...yAxisPropsWithoutLabel} />
                                    <Tooltip
                                        cursor={false}
                                        content={(isOperatorChart || chartId === 'operator-target-trips') ? renderOperatorTripsTooltip : undefined}
                                    />
                                    <Legend />
                                    {props.bars && props.bars.map((bar, index) => (
                                        <Bar
                                            key={index}
                                            dataKey={bar.dataKey}
                                            fill={bar.fill}
                                            name={bar.name}
                                            radius={props.radius}
                                            barSize={maximizedBarSize}
                                            activeBar={false}
                                            cursor="pointer"
                                            onClick={(barData, barIndex, event) => {
                                                try {
                                                    // Stop event propagation to prevent extension interference
                                                    if (event && event.stopPropagation) {
                                                        event.stopPropagation();
                                                    }

                                                    // Enable drill-down for operator and equipment charts
                                                    // barData contains the payload with all data fields
                                                    const payload = barData?.payload || barData;
                                                    console.log('🔍 Bar clicked:', { barData, payload, chartId, isOperatorChart });

                                                    // Handle operator charts (includes operator-target-trips)
                                                    if ((isOperatorChart || chartId === 'operator-target-trips') && payload && payload.name) {
                                                        console.log('🔍 Triggering operator drill-down:', payload.name, payload.operatorId);
                                                        handleOperatorDrillDown(payload.name, payload.operatorId);
                                                    }
                                                    // Handle excavator chart
                                                    else if (chartId === 'excavator-target-trips' && payload && payload.name) {
                                                        console.log('🔍 Triggering equipment drill-down:', payload.name, payload.equipmentId);
                                                        handleEquipmentDrillDown(payload.name, payload.equipmentId);
                                                    }
                                                } catch (err) {
                                                    console.error('Error in bar click handler:', err);
                                                }
                                            }}
                                        >
                                            {(isOperatorChart || chartId === 'operator-target-trips') && data && data.map((entry, idx) => (
                                                <Cell
                                                    key={`cell-${idx}`}
                                                    fill={entry.name === selectedOperatorForDrillDown ? (index === 0 ? "#1e40af" : "#059669") : bar.fill}
                                                    cursor="pointer"
                                                    onClick={(e) => {
                                                        try {
                                                            if (e && e.stopPropagation) e.stopPropagation();
                                                            console.log('🔍 Cell clicked (operator):', entry.name, entry.operatorId);
                                                            handleOperatorDrillDown(entry.name, entry.operatorId);
                                                        } catch (err) {
                                                            console.error('Cell click error:', err);
                                                        }
                                                    }}
                                                />
                                            ))}
                                            {chartId === 'excavator-target-trips' && data && data.map((entry, idx) => (
                                                <Cell
                                                    key={`cell-${idx}`}
                                                    fill={entry.name === selectedEquipmentForDrillDown ? (index === 0 ? "#b45309" : "#6b21a8") : bar.fill}
                                                    cursor="pointer"
                                                    onClick={(e) => {
                                                        try {
                                                            if (e && e.stopPropagation) e.stopPropagation();
                                                            console.log('🔍 Cell clicked (excavator):', entry.name, entry.equipmentId);
                                                            handleEquipmentDrillDown(entry.name, entry.equipmentId);
                                                        } catch (err) {
                                                            console.error('Cell click error:', err);
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Bar>
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Fixed X-axis label - positioned absolutely at the bottom */}
                    {props.xAxis?.label && (
                        <div
                            className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
                            style={{
                                height: '30px',
                                marginLeft: props.yAxis?.label ? '60px' : '0',
                                fontSize: '14px', // Increased from 12px
                                fontWeight: 600,
                                color: '#374151'
                            }}
                        >
                            {props.xAxis.label.value || 'X-Axis'}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const MaximizeButton = ({ chartId, className = "p-0 hover:bg-gray-100 rounded-lg transition-colors" }) => (
        <button
            onClick={() => handleMaximize(chartId)}
            className={className}
            aria-label="Maximize chart"
        >
            <Maximize2 className="w-4 h-4 text-gray-600" />
        </button>
    );

    // Render header actions (dropdown selectors) for maximized charts
    const renderHeaderActions = (chartId) => {
        // Charts that have dropdown selectors
        const chartsWithDropdowns = [
            'equipment-productivity',
            'equipment-availability',
            'operator-availability',
            'operator-productivity-vs-target',
            'equipment-average-availability',
            'equipment-average-utilization',
            'operator-average-utilization',
            'operator-target-trips',
            'excavator-target-trips'
        ];

        if (chartsWithDropdowns.includes(chartId)) {
            const currentSortOrder = sortOrder[chartId] || 'low-high';
            return (
                <Select
                    value={currentSortOrder}
                    onValueChange={(value) => {
                        setSortOrder(prev => ({
                            ...prev,
                            [chartId]: value
                        }));
                    }}
                >
                    <SelectTrigger className="w-[160px] h-9 text-xs bg-gray-50">
                        <SelectValue placeholder="Sort order" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="low-high">Low to High</SelectItem>
                        <SelectItem value="high-low">High to Low</SelectItem>
                    </SelectContent>
                </Select>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col min-h-full">
            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            )}
            {/* Active Drill-down Filters - UI indication */}
            {(selectedEquipmentForDrillDown || selectedOperatorForDrillDown) && (
                <div className="px-3 md:px-4 py-2 bg-gray-50 flex items-center gap-2">
                    <div className="text-[12px] font-medium text-gray-500">Active Drill-down:</div>
                    {selectedEquipmentForDrillDown && (
                        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
                            <span className="font-bold">Equipment:</span> {selectedEquipmentForDrillDown}
                            <button
                                onClick={handleClearDrillDown}
                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                title="Clear equipment filter"
                            >
                                <X className="w-3 h-3 text-blue-700" />
                            </button>
                        </div>
                    )}
                    {selectedOperatorForDrillDown && (
                        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
                            <span className="font-bold">Operator:</span> {selectedOperatorForDrillDown}
                            <button
                                onClick={handleClearOperatorDrillDown}
                                className="hover:bg-emerald-200 rounded-full p-0.5 transition-colors"
                                title="Clear operator filter"
                            >
                                <X className="w-3 h-3 text-emerald-700" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Key Metrics Section */}
            <div className="px-3 md:px-4 pt-2 pb-1 bg-gray-50">
                {/* Top Metrics Row: Total Productivity, Recent Productivity, Target Trips */}
                <div className="flex flex-wrap gap-2">
                    {/* Total Productivity */}
                    {(() => {
                        const totalVal = totalProductivity.value || 0;
                        const targetVal = totalProductivity.target || 0;
                        const pct = Number(totalProductivity.percentage || 0);
                        const diffValue = Math.abs(totalVal - targetVal);
                        const isIncrease = totalVal >= targetVal;
                        // Format large numbers with K/M suffix
                        const formatDiffValue = (val) => {
                            if (val >= 1000000) return `${(val / 1000000).toFixed(2)}M`;
                            if (val >= 1000) return `${(val / 1000).toFixed(2)}K`;
                            return val.toFixed(2);
                        };
                        return (
                            <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[240px] sm:min-w-[300px] flex-1 h-[70px] sm:h-[80px] flex flex-col justify-between">
                                <h3 className="text-[10px] sm:text-xs font-bold font-open-sans text-neutral-950 mb-1">Total Productivity</h3>
                                <div className="flex items-center gap-1.5">
                                    <div className="text-sm sm:text-lg font-bold font-open-sans text-gray-900 leading-tight">
                                        {totalVal.toFixed(2)}
                                        <span className="text-[10px] sm:text-[12px] font-normal text-gray-600 ml-1">
                                            {totalProductivity.unit || 'CLM'}
                                        </span>
                                    </div>
                                    <span
                                        className="text-[9px] sm:text-xs font-medium rounded px-1 sm:px-1.5 py-0.5 bg-green-100"
                                        style={{
                                            color: 'rgb(0,105,0)'
                                        }}
                                    >
                                        {Math.abs(pct).toFixed(2)}%
                                    </span>
                                    <span
                                        className="text-xs sm:text-sm font-bold font-open-sans leading-tight rounded px-1.5 py-0"
                                        style={{
                                            backgroundColor: diffValue === 0 ? '#f3f4f6' : isIncrease ? '#bbf7d0' : '#fecaca',
                                            color: '#111827'
                                        }}
                                    >
                                        {formatDiffValue(diffValue)}
                                        <span className="text-[10px] sm:text-[12px] font-normal ml-1">
                                            {totalProductivity.unit || 'CLM'}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Recent Productivity Cards */}
                    <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-full sm:min-w-[220px] flex-[2] h-auto sm:h-[80px] flex flex-col justify-between">
                        <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Recent Productivity</h3>
                        <div className="flex justify-between w-full">
                            {recentProductivity && recentProductivity.length > 0 ? (
                                recentProductivity.map((item) => (
                                    <div key={item.id || Math.random()} className="flex flex-col items-start">
                                        <div className="flex items-center gap-1">
                                            <span className="text-base font-bold text-gray-900">
                                                {item.current || 0}<span style={{ fontSize: '11px' }}>/{item.target || 0}</span>
                                            </span>
                                            <span className="text-[11px] font-normal text-gray-600">{item.unit || 'CLM'}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-0.9">
                                            <span
                                                className="px-1 py-0.5 rounded text-[10px] font-medium bg-green-100"
                                                style={{
                                                    color: 'rgb(0,105,0)'
                                                }}
                                            >
                                                {Math.abs(item.percentage || 0)}%
                                            </span>
                                            <span className="text-[10px] text-gray-500">{item.date || 'Date'}</span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-500 text-sm">No data available</div>
                            )}
                        </div>
                    </div>

                    {/* Target Trips Chart */}
                    <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] sm:min-w-[180px] flex-1 h-[70px] sm:h-[80px] flex items-center overflow-hidden">
                        <div className="flex items-center w-full gap-1">
                            <div className="relative" style={{ width: 62, height: 60 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Tooltip
                                            formatter={(value) => [`${value}%`]}
                                            contentStyle={{ fontSize: '10px', borderRadius: '4px', padding: '2px 6px' }}
                                        />
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={18}
                                            outerRadius={28}
                                            paddingAngle={2}
                                            dataKey="value"
                                            startAngle={90}
                                            endAngle={-270}
                                            stroke="none"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>

                            </div>

                            <div className="flex-1 flex flex-col justify-center gap-0.5 ml-1">
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center text-[10px] text-gray-500 font-medium w-[70px]">
                                        <span className="w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: '#fdb205ff' }}></span>Target Trips
                                    </div>
                                    <div className="text-[10px] font-bold text-gray-900">
                                        {targetTrips.target?.toLocaleString() || '0'}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center text-[10px] text-gray-500 font-medium w-[70px]">
                                        <span className="w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: '#00498A' }}></span>Achieved
                                    </div>
                                    <div className="text-[10px] font-bold text-gray-900">
                                        {targetTrips.actual?.toLocaleString() || '0'}
                                        <span className="ml-1 font-normal text-gray-500">
                                            ({targetTrips.target > 0 ? (targetTrips.actual / targetTrips.target * 100).toFixed(2) : '0.00'}%)
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center text-[10px] text-gray-500 font-medium w-[70px] pl-2.5">
                                        Shortfall
                                    </div>
                                    <div className="text-[10px] font-bold text-gray-900">
                                        {targetTrips.blended?.toLocaleString() || '0'}
                                        <span className="ml-1 font-normal text-gray-500">
                                            ({targetTrips.target > 0 ? (targetTrips.blended / targetTrips.target * 100).toFixed(2) : '0.00'}%)
                                        </span>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* END FIXED KEY METRICS SECTION */}

            {/* Charts Section */}
            <div className="flex-1 px-3 md:px-4 pb-4 bg-gray-50">
                {/* All Charts - Single Grid (1-9 columns responsive) */}
                <div className="grid gap-3 pt-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {/* Equipment Productivity vs Target */}
                    <div className="bg-white rounded-lg shadow-sm p-0 h-[160px] border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{equipmentTypeLabel} Productivity vs Target</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['equipment-productivity'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({
                                            ...prev,
                                            'equipment-productivity': value
                                        }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="equipment-productivity" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            {/* Fixed Y-axis label - positioned absolutely on the left */}
                            <div
                                className="absolute left-2 top-0 bottom-14 z-30 pointer-events-none flex items-center"
                                style={{
                                    width: '60px',
                                    paddingLeft: '0px'
                                }}
                            >
                                <div
                                    style={{
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'mixed',
                                        transform: 'rotate(180deg)',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        color: '#374151',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Productivity Target
                                </div>
                            </div>

                            {/* Scrollable chart container */}
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '20px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = equipmentProductivityData || [];

                                    // Apply sorting based on sortOrder state
                                    const currentSortOrder = sortOrder['equipment-productivity'] || 'low-high';
                                    if (currentSortOrder && data && data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.productivity) || 0;
                                            const bValue = Number(b.productivity) || 0;
                                            if (currentSortOrder === 'low-high') {
                                                return aValue - bValue;
                                            } else {
                                                return bValue - aValue;
                                            }
                                        });
                                    }

                                    // Calculate minimum width based on number of bars
                                    // Each bar needs approximately 80px (bar width 34.94 + gap 10%)
                                    const estimatedWidthPerBar = 80;
                                    const minChartWidth = Math.max(512, data.length * estimatedWidthPerBar);

                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-equipment-productivity-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -30, bottom: 14 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis
                                                        dataKey="name"
                                                        tick={{ fontSize: 12 }}
                                                        interval={0}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <Tooltip cursor={false} />
                                                    <ReferenceLine y={90} stroke="#ef4444" strokeDasharray="3 3" />
                                                    <Bar
                                                        dataKey="productivity"
                                                        fill="#3b82f6"
                                                        radius={[4, 4, 0, 0]}
                                                        barSize={34.94}
                                                        activeBar={false}
                                                        onClick={(data) => {
                                                            if (data && data.name) {
                                                                handleEquipmentDrillDown(data.name, data.equipmentId);
                                                            }
                                                        }}
                                                    >
                                                        {data.map((entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={entry.name === selectedEquipmentForDrillDown ? "#1e40af" : "#3b82f6"}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleEquipmentDrillDown(entry.name, entry.equipmentId)}
                                                            />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Fixed X-axis label - positioned absolutely at the bottom */}
                            <div
                                className="absolute bottom-4 left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
                                style={{
                                    height: '30px',
                                    marginLeft: '20px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: '#374151'
                                }}
                            >
                                {equipmentTypeLabel}
                            </div>


                        </div>
                    </div>
                    {/* Equipment Availability */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{equipmentTypeLabel} Availability</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['equipment-availability'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({
                                            ...prev,
                                            'equipment-availability': value
                                        }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="equipment-availability" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            {/* Fixed Y-axis label */}
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Availability
                                </div>
                            </div>
                            {/* Scrollable chart container */}
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '20px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = equipmentAvailabilityData || [];
                                    const currentSortOrder = sortOrder['equipment-availability'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.availability) || 0;
                                            const bValue = Number(b.availability) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    const minChartWidth = Math.max(512, data.length * 80);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-equipment-availability-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -35, bottom: 14 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip cursor={false} />
                                                    <ReferenceLine y={95} stroke="#10b981" strokeDasharray="3 3" />
                                                    <Bar dataKey="availability" fill="#14b8a6" radius={[4, 4, 0, 0]} barSize={34.94} activeBar={false} onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId); }}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.name === selectedEquipmentForDrillDown ? "#0d9488" : "#14b8a6"} style={{ cursor: 'pointer' }} onClick={() => handleEquipmentDrillDown(entry.name, entry.equipmentId)} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            {/* Fixed X-axis label */}
                            <div className="absolute bottom-4 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {equipmentTypeLabel}
                            </div>
                        </div>
                    </div>
                    {/* Operator Productivity vs Target */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{operatorTypeLabel} Productivity vs Target</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['operator-productivity'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'operator-productivity': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="operator-productivity-vs-target" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Productivity Target
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = operatorProductivityData || [];
                                    const currentSortOrder = sortOrder['operator-productivity'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.productivity) || 0;
                                            const bValue = Number(b.productivity) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    // Calculate dynamic width per bar based on longest label - ensure full labels fit
                                    const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                                    // Calculate width to fit full labels: ~10px per character for horizontal text
                                    const widthPerBar = Math.max(120, maxLabelLength * 10);
                                    const minChartWidth = Math.max(512, data.length * widthPerBar);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-operator-productivity-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -30, bottom: 12 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip
                                                        cursor={false}
                                                        content={({ active, payload }) => {
                                                            if (!active || !payload || !payload.length) return null;
                                                            const entry = payload[0].payload;
                                                            return (
                                                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 150 }}>
                                                                    {entry.empCode && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}><span style={{ fontWeight: 600, color: '#374151' }}>Emp Code:</span> {entry.empCode}</div>}
                                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}><span style={{ fontWeight: 600, color: '#374151' }}>Name:</span> {entry.name}</div>
                                                                    {payload.map((p, i) => (
                                                                        <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                                            <span style={{ color: p.color || '#374151' }}>{p.name}</span>
                                                                            <span style={{ fontWeight: 700, color: '#111827' }}>{p.value}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <ReferenceLine y={1.15} stroke="#ef4444" strokeDasharray="3 3" />
                                                    <Bar dataKey="productivity" fill="#0594bc" radius={[4, 4, 0, 0]} barSize={34.94} activeBar={false} onClick={(data) => { if (data && data.name) handleOperatorDrillDown(data.name, data.operatorId); }}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.name === selectedOperatorForDrillDown ? "#0369a1" : "#0594bc"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-4 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {operatorTypeLabel}
                            </div>
                        </div>
                    </div>

                    {/* Operator Availability */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{operatorTypeLabel} Availability</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['operator-availability'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'operator-availability': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="operator-availability" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Average Utilization
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = operatorAvailabilityData || [];
                                    const currentSortOrder = sortOrder['operator-availability'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.averageUtilization) || 0;
                                            const bValue = Number(b.averageUtilization) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    // Calculate dynamic width per bar based on longest label - ensure full labels fit
                                    const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                                    // Calculate width to fit full labels: ~10px per character for horizontal text
                                    const widthPerBar = Math.max(120, maxLabelLength * 10);
                                    const minChartWidth = Math.max(512, data.length * widthPerBar);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-operator-availability-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -35, bottom: 12 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip
                                                        cursor={false}
                                                        content={({ active, payload }) => {
                                                            if (!active || !payload || !payload.length) return null;
                                                            const entry = payload[0].payload;
                                                            return (
                                                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 150 }}>
                                                                    {entry.empCode && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}><span style={{ fontWeight: 600, color: '#374151' }}>Emp Code:</span> {entry.empCode}</div>}
                                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}><span style={{ fontWeight: 600, color: '#374151' }}>Name:</span> {entry.name}</div>
                                                                    {payload.map((p, i) => (
                                                                        <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                                            <span style={{ color: p.color || '#374151' }}>{p.name}</span>
                                                                            <span style={{ fontWeight: 700, color: '#111827' }}>{p.value}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <ReferenceLine y={550} stroke="#ef4444" strokeDasharray="3 3" />
                                                    <Bar dataKey="averageUtilization" fill="#14b8a6" radius={[4, 4, 0, 0]} barSize={34.94} activeBar={false} onClick={(data) => { if (data && data.name) handleOperatorDrillDown(data.name, data.operatorId); }}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.name === selectedOperatorForDrillDown ? "#0d9488" : "#14b8a6"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-4 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {operatorTypeLabel}
                            </div>
                        </div>
                    </div>
                    {/* Equipment Average Availability */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{equipmentTypeLabel} Average Availability</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['equipment-avg-availability'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'equipment-avg-availability': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="equipment-average-availability" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Productivity Target
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = equipmentAverageAvailabilityData || [];
                                    const currentSortOrder = sortOrder['equipment-avg-availability'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.productivityTarget) || 0;
                                            const bValue = Number(b.productivityTarget) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    const minChartWidth = Math.max(512, data.length * 80);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-equipment-avg-availability-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -35, bottom: 6 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip cursor={false} />
                                                    <ReferenceLine y={1.15} stroke="#ef4444" strokeDasharray="3 3" />
                                                    <Bar dataKey="productivityTarget" fill="#60a5fa" radius={[4, 4, 0, 0]} barSize={34.94} activeBar={false} onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId); }}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.name === selectedEquipmentForDrillDown ? "#3b82f6" : "#60a5fa"} style={{ cursor: 'pointer' }} onClick={() => handleEquipmentDrillDown(entry.name, entry.equipmentId)} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {equipmentTypeLabel}
                            </div>
                        </div>
                    </div>

                    {/* Equipment Average Utilization */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{equipmentTypeLabel} Average Utilization</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['equipment-avg-utilization'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'equipment-avg-utilization': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="equipment-average-utilization" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Average Utilization
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = equipmentAverageUtilizationData || [];
                                    const currentSortOrder = sortOrder['equipment-avg-utilization'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.averageUtilization) || 0;
                                            const bValue = Number(b.averageUtilization) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    const minChartWidth = Math.max(512, data.length * 80);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-equipment-avg-utilization-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -30, bottom: 6 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip cursor={false} />
                                                    <ReferenceLine y={1.05} stroke="#ef4444" strokeDasharray="3 3" />
                                                    <Bar dataKey="averageUtilization" fill="#f97316" radius={[4, 4, 0, 0]} barSize={34.94} activeBar={false} onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId); }}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.name === selectedEquipmentForDrillDown ? "#ea580c" : "#f97316"} style={{ cursor: 'pointer' }} onClick={() => handleEquipmentDrillDown(entry.name, entry.equipmentId)} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {equipmentTypeLabel}
                            </div>
                        </div>
                    </div>
                    {/* Operator Average Utilization */}
                    <div className="bg-white rounded-lg shadow-sm p-0 border border-gray-200 h-[160px] flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">{operatorTypeLabel} Average Utilization</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['operator-avg-utilization'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'operator-avg-utilization': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="operator-average-utilization" className="p-1.5 hover:bg-gray-100 rounded transition-colors" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Average Utilization
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = operatorAverageUtilizationData || [];
                                    const currentSortOrder = sortOrder['operator-avg-utilization'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.productivity) || 0;
                                            const bValue = Number(b.productivity) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    // Calculate dynamic width per bar based on longest label - ensure full labels fit
                                    const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                                    // Calculate width to fit full labels: ~10px per character for horizontal text
                                    const widthPerBar = Math.max(120, maxLabelLength * 10);
                                    const minChartWidth = Math.max(512, data.length * widthPerBar);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-operator-avg-utilization-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barSize={34.94} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -30, bottom: 6 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip
                                                        cursor={false}
                                                        content={({ active, payload }) => {
                                                            if (!active || !payload || !payload.length) return null;
                                                            const entry = payload[0].payload;
                                                            return (
                                                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 150 }}>
                                                                    {entry.empCode && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}><span style={{ fontWeight: 600, color: '#374151' }}>Emp Code:</span> {entry.empCode}</div>}
                                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}><span style={{ fontWeight: 600, color: '#374151' }}>Name:</span> {entry.name}</div>
                                                                    {payload.map((p, i) => (
                                                                        <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                                            <span style={{ color: p.color || '#374151' }}>{p.name}</span>
                                                                            <span style={{ fontWeight: 700, color: '#111827' }}>{p.value}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <ReferenceLine y={90} stroke="#ef4444" strokeWidth={2} />
                                                    <Bar dataKey="productivity" fill="#0594bc" radius={[4, 4, 0, 0]} activeBar={false} onClick={(data) => { if (data && data.name) handleOperatorDrillDown(data.name, data.operatorId); }}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.name === selectedOperatorForDrillDown ? "#0369a1" : "#0594bc"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {operatorTypeLabel}
                            </div>
                        </div>
                    </div>

                    {/* Target trips and achieved by operator name */}
                    <div className="bg-white rounded-lg shadow-sm p-0 border border-gray-200 h-[160px] flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Target trips and achieved by {operatorTypeLabel} name</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['operator-target-trips'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'operator-target-trips': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="operator-target-trips" className="p-1.5 hover:bg-gray-100 rounded transition-colors" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Trips
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = operatorTargetTripsData || [];
                                    const currentSortOrder = sortOrder['operator-target-trips'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.targetTrips) || 0;
                                            const bValue = Number(b.targetTrips) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    // Calculate dynamic width per bar based on longest label - ensure full labels fit
                                    const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                                    // Calculate width to fit full labels: ~10px per character for horizontal text
                                    const widthPerBar = Math.max(120, maxLabelLength * 10);
                                    const minChartWidth = Math.max(512, data.length * widthPerBar);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-operator-target-trips-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barSize={34.94} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -30, bottom: -20 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip
                                                        cursor={false}
                                                        content={renderOperatorTripsTooltip}
                                                    />
                                                    <Legend />
                                                    <Bar
                                                        dataKey="targetTrips"
                                                        fill="#3b82f6"
                                                        name="Target Trips"
                                                        radius={[4, 4, 0, 0]}
                                                        activeBar={false}
                                                        onClick={(data) => { if (data && data.name) handleOperatorDrillDown(data.name, data.operatorId); }}
                                                    >
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-target-${index}`} fill={entry.name === selectedOperatorForDrillDown ? "#1e40af" : "#3b82f6"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                    <Bar
                                                        dataKey="achievedTrips"
                                                        fill="#10b981"
                                                        name="Achieved Trips"
                                                        radius={[4, 4, 0, 0]}
                                                        activeBar={false}
                                                        onClick={(data) => { if (data && data.name) handleOperatorDrillDown(data.name, data.operatorId); }}
                                                    >
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-achieved-${index}`} fill={entry.name === selectedOperatorForDrillDown ? "#059669" : "#10b981"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {operatorTypeLabel}
                            </div>
                        </div>
                    </div>

                    {/* Target trips and achieved by Excavator */}
                    <div className="bg-white rounded-lg shadow-sm p-0 border border-gray-200 h-[160px] flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Target trips and achieved by {excavatorTypeLabel}</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={sortOrder['excavator-target-trips'] || 'low-high'}
                                    onValueChange={(value) => {
                                        setSortOrder(prev => ({ ...prev, 'excavator-target-trips': value }));
                                    }}
                                >
                                    <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low-high">Low to High</SelectItem>
                                        <SelectItem value="high-low">High to Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <MaximizeButton chartId="excavator-target-trips" className="p-1.5 hover:bg-gray-100 rounded transition-colors" />
                            </div>
                        </div>
                        <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
                            <div className="absolute left-[-5px] top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                                    Trips
                                </div>
                            </div>
                            <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '30px', marginBottom: '-17px' }}>
                                {(() => {
                                    let data = excavatorTargetTripsData || [];
                                    const currentSortOrder = sortOrder['excavator-target-trips'] || 'low-high';
                                    if (data.length > 0) {
                                        data = [...data].sort((a, b) => {
                                            const aValue = Number(a.targetTrips) || 0;
                                            const bValue = Number(b.targetTrips) || 0;
                                            return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                                        });
                                    }
                                    // Calculate dynamic width - excavators typically have shorter names
                                    const minChartWidth = Math.max(512, data.length * 80);
                                    return (
                                        <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-excavator-target-trips-${currentSortOrder}`}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} barSize={34.94} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -30, bottom: -20 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                    <Tooltip
                                                        cursor={false}
                                                        formatter={(value, name) => [value, name]}
                                                        labelFormatter={(label) => (
                                                            <div className="flex flex-col gap-0 flex-shrink-0">
                                                                <span className="font-bold text-[11px] text-slate-900 leading-tight">{label}</span>
                                                            </div>
                                                        )}
                                                    />
                                                    <Legend />
                                                    <Bar
                                                        dataKey="targetTrips"
                                                        fill="#f59e0b"
                                                        name="Target Trips"
                                                        radius={[4, 4, 0, 0]}
                                                        activeBar={false}
                                                        onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId); }}
                                                    >
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-target-${index}`} fill={entry.name === selectedEquipmentForDrillDown ? "#b45309" : "#f59e0b"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                    <Bar
                                                        dataKey="achievedTrips"
                                                        fill="#8b5cf6"
                                                        name="Achieved Trips"
                                                        radius={[4, 4, 0, 0]}
                                                        activeBar={false}
                                                        onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId); }}
                                                    >
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-achieved-${index}`} fill={entry.name === selectedEquipmentForDrillDown ? "#6b21a8" : "#8b5cf6"} style={{ cursor: 'pointer' }} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                {excavatorTypeLabel}
                            </div>
                        </div>
                    </div>

                    {/* Productivity Forecast */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Productivity Forecast Next 10 Days</h3>
                            <MaximizeButton chartId="productivity-forecast" />
                        </div>
                        <div className="flex-1 p-1 pt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={productivityForecastData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Date', position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fontSize: 12 } }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Productivity', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                                    />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="current"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="forecast"
                                        stroke="#f97316"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex items-center justify-center gap-6 px-6 pb-2">
                            <div className="flex items-center text-xs text-gray-600">
                                <span className="w-3 h-3 rounded bg-[#3b82f6] mr-1 inline-block"></span>
                                Current
                            </div>
                            <div className="flex items-center text-xs text-gray-600">
                                <span className="w-3 h-3 rounded bg-[#f97316] mr-1 inline-block"></span>
                                Forecast
                            </div>
                        </div>
                    </div>

                    {/* Availability Forecast */}
                    <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Availability Forecast Next 10 Days</h3>
                            <MaximizeButton chartId="availability-forecast" />
                        </div>
                        <div className="flex-1 p-1 pt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={availabilityForecastData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Date', position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fontSize: 12 } }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Availability', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                                    />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="current"
                                        stroke="#14b8a6"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="forecast"
                                        stroke="#f97316"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex items-center justify-center gap-6 px-6 pb-2">
                            <div className="flex items-center text-xs text-gray-600">
                                <span className="w-3 h-3 rounded bg-[#14b8a6] mr-1 inline-block"></span>
                                Current
                            </div>
                            <div className="flex items-center text-xs text-gray-600">
                                <span className="w-3 h-3 rounded bg-[#f97316] mr-1 inline-block"></span>
                                Forecast
                            </div>
                        </div>
                    </div>
                    {/* Utilization Forecast Next 10 days */}
                    <div className="bg-white rounded-lg shadow-sm p-0 border border-gray-200 h-[160px] flex flex-col">
                        <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
                            <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Utilization Forecast Next 10 days</h3>
                            <MaximizeButton chartId="utilization-forecast" />
                        </div>
                        <div className="flex-1 p-1 pt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={utilizationForecastData || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Date', position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fontSize: 12 } }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Utilization', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                                    />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="current"
                                        stroke="#0594bc"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="forecast"
                                        stroke="#f97316"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex items-center justify-center gap-6 px-6 pb-2">
                            <div className="flex items-center text-xs text-gray-600">
                                <span className="w-3 h-3 rounded bg-[#0594bc] mr-1 inline-block"></span>
                                Current
                            </div>
                            <div className="flex items-center text-xs text-gray-600">
                                <span className="w-3 h-3 rounded bg-[#f97316] mr-1 inline-block"></span>
                                Forecast
                            </div>
                        </div>
                    </div>
                </div>
                {/* ----- END Operator Utilization Row ----- */}
            </div>
            {/* END Scrollable Charts Section */}

            {/* Chart Modal for Maximized View */}
            {maximizedChartId && (
                <ChartModal
                    isOpen={!!maximizedChartId}
                    onClose={handleCloseModal}
                    title={chartConfigs[maximizedChartId]?.title || 'Chart'}
                    headerActions={renderHeaderActions(maximizedChartId)}
                    showEquipmentFilter={chartConfigs[maximizedChartId]?.chartType === 'bar' || chartConfigs[maximizedChartId]?.chartType === 'clusteredBar'}
                    filterLabel={maximizedChartId?.includes('operator') ? 'All Operator' : 'All Equipment'}
                    equipmentList={getEquipmentListForChart(maximizedChartId)}
                    selectedEquipment={modalEquipmentFilter}
                    onEquipmentChange={setModalEquipmentFilter}
                >
                    {renderMaximizedChart(maximizedChartId)}
                </ChartModal>
            )}
        </div>
    );
};

export default Productivity;
